import { useState } from "react";
import type { WebsiteIntentRule, WebsiteSite } from "@sales-ops-amplify/shared";
import {
  useCreateWebsiteSite,
  useDeleteWebsiteSite,
  useSeedVisitorSamples,
  useTestWebsiteConnection,
  useUpdateVisitorPreferences,
  useUpdateWebsiteSite,
  useVisitorPreferences,
  useWebsiteScript,
  useWebsiteSites,
} from "@/hooks/useVisitors";
import { cn } from "@/cn";

function IntentRulesEditor({
  rules,
  onChange,
}: {
  rules: WebsiteIntentRule[];
  onChange: (rules: WebsiteIntentRule[]) => void;
}) {
  return (
    <div className="space-y-2">
      {rules.map((r, idx) => (
        <div key={r.id} className="flex flex-wrap items-center gap-2">
          <input
            className="w-48 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={r.pathPattern}
            placeholder="/pricing"
            onChange={(e) => {
              const next = [...rules];
              next[idx] = { ...r, pathPattern: e.target.value };
              onChange(next);
            }}
          />
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={r.intent}
            onChange={(e) => {
              const next = [...rules];
              next[idx] = {
                ...r,
                intent: e.target.value as WebsiteIntentRule["intent"],
              };
              onChange(next);
            }}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => onChange(rules.filter((_, i) => i !== idx))}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-xs text-brand hover:underline"
        onClick={() =>
          onChange([
            ...rules,
            {
              id: crypto.randomUUID(),
              pathPattern: "/pricing",
              intent: "high",
            },
          ])
        }
      >
        Add intent rule
      </button>
    </div>
  );
}

function SiteCard({ site }: { site: WebsiteSite }) {
  const script = useWebsiteScript(site.siteId);
  const update = useUpdateWebsiteSite();
  const del = useDeleteWebsiteSite();
  const test = useTestWebsiteConnection();
  const [copied, setCopied] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-foreground">{site.domain}</p>
          <p className="text-xs text-muted-foreground">
            Status:{" "}
            <span
              className={cn(
                site.status === "active"
                  ? "text-emerald-500"
                  : "text-muted-foreground",
              )}
            >
              {site.status}
            </span>
            {site.lastSeenAt
              ? ` · last seen ${new Date(site.lastSeenAt).toLocaleString()}`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md border border-border px-2.5 py-1 text-xs"
            disabled={test.isPending}
            onClick={() => test.mutate(site.siteId)}
          >
            Test connection
          </button>
          <button
            type="button"
            className="rounded-md border border-border px-2.5 py-1 text-xs"
            onClick={() =>
              update.mutate({
                siteId: site.siteId,
                patch: {
                  status: site.status === "disabled" ? "pending" : "disabled",
                },
              })
            }
          >
            {site.status === "disabled" ? "Enable" : "Disable"}
          </button>
          <button
            type="button"
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs text-destructive"
            onClick={() => del.mutate(site.siteId)}
          >
            Delete
          </button>
        </div>
      </div>

      <label className="block text-xs text-muted-foreground">
        Identification mode
        <select
          className="mt-1 block rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
          value={site.identificationMode}
          onChange={(e) =>
            update.mutate({
              siteId: site.siteId,
              patch: {
                identificationMode: e.target
                  .value as WebsiteSite["identificationMode"],
              },
            })
          }
        >
          <option value="company">Company only</option>
          <option value="company_and_person">Company &amp; person</option>
        </select>
      </label>

      <div>
        <p className="mb-1 text-xs text-muted-foreground">Intent page rules</p>
        <IntentRulesEditor
          rules={site.intentRules ?? []}
          onChange={(intentRules) =>
            update.mutate({ siteId: site.siteId, patch: { intentRules } })
          }
        />
      </div>

      <div>
        <p className="mb-1 text-xs text-muted-foreground">Tracking script</p>
        <textarea
          readOnly
          rows={2}
          className="w-full rounded-md border border-border bg-muted/20 px-2 py-1.5 font-mono text-xs text-foreground"
          value={script.data?.embed ?? "Loading…"}
        />
        <button
          type="button"
          className="mt-1 text-xs text-brand hover:underline"
          disabled={!script.data?.embed}
          onClick={async () => {
            if (!script.data?.embed) return;
            await navigator.clipboard.writeText(script.data.embed);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "Copied" : "Copy code"}
        </button>
      </div>
    </div>
  );
}

export function VisitorsSettingsPanel({ compact }: { compact?: boolean }) {
  const sites = useWebsiteSites();
  const prefs = useVisitorPreferences();
  const create = useCreateWebsiteSite();
  const updatePrefs = useUpdateVisitorPreferences();
  const seed = useSeedVisitorSamples();
  const [domain, setDomain] = useState("");

  return (
    <div className={cn("space-y-6", compact && "space-y-4")}>
      {!compact ? (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Website tracking
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Add domains, install the first-party snippet, and score high-intent
            pages. Identification matches known contacts (email click handoff)
            and company domains in your workspace — not reverse-IP.
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.data?.enabled ?? true}
            onChange={(e) =>
              updatePrefs.mutate({ enabled: e.target.checked })
            }
          />
          Enable website visitor identification
        </label>
        <button
          type="button"
          className="rounded-md border border-border px-3 py-1.5 text-sm"
          disabled={seed.isPending}
          onClick={() => seed.mutate()}
        >
          Generate sample visits
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-muted-foreground">
          Domain
          <input
            className="mt-1 block w-64 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            placeholder="acme.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground"
          disabled={create.isPending || !domain.trim()}
          onClick={async () => {
            await create.mutateAsync({
              domain: domain.trim(),
              identificationMode: "company_and_person",
            });
            setDomain("");
          }}
        >
          Add website
        </button>
      </div>

      {sites.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading sites…</p>
      ) : (sites.data?.sites ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No domains yet. Add a website to get an install snippet.
        </p>
      ) : (
        <div className="space-y-4">
          {(sites.data?.sites ?? []).map((s) => (
            <SiteCard key={s.siteId} site={s} />
          ))}
        </div>
      )}
    </div>
  );
}
